// NEW_FILE_JUSTIFIED: Reusable policy file drop-zone for the Extract page — no existing equivalent; lifts pattern from DocumentUploadDialog
import { useRef, useState } from 'react';
import { UploadCloud, FileText, Image as ImageIcon, X, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { IPolicyDropzoneProps } from '@/@types/insuranceBuilder';

const ACCEPTED_MIME = ['application/pdf', 'image/jpeg', 'image/png'];
const ACCEPTED_EXTENSIONS = '.pdf,.jpg,.jpeg,.png';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function isAccepted(file: File): boolean {
  if (ACCEPTED_MIME.includes(file.type)) return true;
  // Phone-scan apps occasionally drop type=''; fall back to extension
  return /\.(pdf|jpe?g|png)$/i.test(file.name);
}

export function PolicyDropzone({
  onFileSelected,
  onRemove,
  selectedFile,
  disabled = false,
  maxSizeMB = 10,
}: IPolicyDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = (file: File) => {
    setError(null);
    if (!isAccepted(file)) {
      setError('Only PDF, JPG, or PNG files are accepted.');
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`File exceeds ${maxSizeMB} MB limit.`);
      return;
    }
    onFileSelected({ file, sizeBytes: file.size, mimeType: file.type });
  };

  if (selectedFile) {
    const isImage = selectedFile.mimeType.startsWith('image/');
    return (
      <div className="rounded-xl border bg-card p-5 flex items-center gap-4">
        <div className="rounded-lg bg-muted p-3">
          {isImage ? (
            <ImageIcon className="h-6 w-6 text-foreground" aria-hidden="true" />
          ) : (
            <FileText className="h-6 w-6 text-foreground" aria-hidden="true" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{selectedFile.file.name}</p>
          <p className="text-xs text-muted-foreground">{formatBytes(selectedFile.sizeBytes)}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          disabled={disabled}
          aria-label="Remove file"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div
        data-dropzone
        onDragOver={e => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => {
          e.preventDefault();
          setDragOver(false);
          if (disabled) return;
          const file = e.dataTransfer.files[0];
          if (file) handleFile(file);
        }}
        className={cn(
          'rounded-xl border-2 border-dashed p-10 flex flex-col items-center justify-center gap-3 text-center transition',
          dragOver && 'border-primary bg-primary/5',
          !dragOver && 'border-border bg-background-secondary',
          disabled && 'opacity-50'
        )}
      >
        <div className="rounded-full bg-primary/15 p-3">
          <UploadCloud className="h-8 w-8 text-primary" aria-hidden="true" />
        </div>
        <p className="text-base font-semibold text-foreground">Drop your policy file here</p>
        <p className="text-sm text-muted-foreground">PDF, JPG, or PNG · up to {maxSizeMB} MB</p>
        <Button
          type="button"
          variant="default"
          size="sm"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          Choose file
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS}
          className="hidden"
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = '';
          }}
        />
      </div>
      {error && (
        <p role="alert" className="flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
}
